import React, { useEffect, useState, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { marketAPI, adminAPI, feedbackAPI, adminSettlementsAPI, walletAPI } from '../services/api';
import AdminAcademy from '../components/admin/AdminAcademy';
import { formatBusinessTypes } from '../constants/businessTypes';
import LoadingSpinner from '../components/common/LoadingSpinner';
import toast from 'react-hot-toast';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import {
  HomeIcon,
  UsersIcon,
  ShoppingBagIcon,
  CurrencyDollarIcon,
  ChatBubbleLeftRightIcon,
  ChevronRightIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  BuildingStorefrontIcon,
  CubeIcon,
  TruckIcon,
  BanknotesIcon,
  WalletIcon,
  ExclamationTriangleIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  PhotoIcon,
  ChartBarIcon
} from '@heroicons/react/24/outline';

// Main categories
const CATEGORIES = [
  { id: 'dashboard', name: 'Dashboard', icon: HomeIcon },
  { id: 'users', name: 'Users', icon: UsersIcon },
  { id: 'marketplace', name: 'Marketplace', icon: ShoppingBagIcon },
  { id: 'financial', name: 'Financial', icon: CurrencyDollarIcon },
  { id: 'content', name: 'Content', icon: ChatBubbleLeftRightIcon },
];

// Sub-tabs per category
const SUB_TABS = {
  dashboard: [{ id: 'overview', name: 'Overview' }],
  users: [
    { id: 'all-users', name: 'All Users' },
    { id: 'farmers', name: 'Farmers' },
    { id: 'suppliers', name: 'Suppliers' },
    { id: 'clients', name: 'Clients' },
  ],
  marketplace: [
    { id: 'supplier-products', name: 'Supplier Products' },
  ],
  financial: [
    { id: 'withdrawals', name: 'Withdrawal Requests' },
    { id: 'escrows', name: 'Escrow Management' },
    { id: 'earnings', name: 'Platform Earnings' },
  ],
  content: [
    { id: 'feedback', name: 'Feedback' },
    { id: 'academy', name: 'Academy' },
  ],
};

const Admin = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  
  // Navigation state
  const [activeCategory, setActiveCategory] = useState('dashboard');
  const [activeSubTab, setActiveSubTab] = useState('overview');
  
  // Data state
  const [dashboardData, setDashboardData] = useState(null);
  const [revenueOrdersChart, setRevenueOrdersChart] = useState([]);
  const [userDistributionChart, setUserDistributionChart] = useState([]);
  const [subscriptionStatusChart, setSubscriptionStatusChart] = useState([]);
  const [userGrowthChart, setUserGrowthChart] = useState([]);
  const [users, setUsers] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [suppliersSummary, setSuppliersSummary] = useState(null);
  const [supplierProducts, setSupplierProducts] = useState([]);
  const [supplierProductsSummary, setSupplierProductsSummary] = useState(null);
  // Legacy products removed - no longer needed
  const [orders, setOrders] = useState([]);
  const [omabraEarnings, setOmabraEarnings] = useState(null);
  const [feedback, setFeedback] = useState([]);
  const [adminWithdrawals, setAdminWithdrawals] = useState([]);
  const [adminEscrows, setAdminEscrows] = useState([]);
  
  // Pagination state
  const [userPage, setUserPage] = useState(1);
  const [supplierPage, setSupplierPage] = useState(1);
  const [supplierProductPage, setSupplierProductPage] = useState(1);
  const [orderPage, setOrderPage] = useState(1);
  const [withdrawalPage, setWithdrawalPage] = useState(1);
  const [escrowPage, setEscrowPage] = useState(1);
  const [earningsPage, setEarningsPage] = useState(1);
  const [feedbackPage, setFeedbackPage] = useState(1);
  const pageSize = 10;
  
  // Filter state
  const [userFilters, setUserFilters] = useState({ role: '', subscription: '', accountType: '', search: '' });
  const [supplierFilters, setSupplierFilters] = useState({ verified: '', active: '', businessType: '', search: '' });
  const [supplierProductFilters, setSupplierProductFilters] = useState({ approved: '', active: '', category: '', search: '' });
  const [orderFilters, setOrderFilters] = useState({ status: '', search: '' });
  const [withdrawalFilters, setWithdrawalFilters] = useState({ status: '', search: '' });
  const [escrowFilters, setEscrowFilters] = useState({ status: 'holding', orderType: '', search: '' }); // Default to 'holding'
  const [earningsPeriod, setEarningsPeriod] = useState('month');
  
  // Modal state
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [processing, setProcessing] = useState(null);
  const [completeWithdrawalModal, setCompleteWithdrawalModal] = useState(null);
  const [orderDetail, setOrderDetail] = useState(null);
  const [editProduct, setEditProduct] = useState(null);
  const [showAddProduct, setShowAddProduct] = useState(false);

  const isAdmin = user?.role === 'admin';

  // Load data based on active tab
  useEffect(() => {
    if (!isAdmin) return;
    loadData();
  }, [activeCategory, activeSubTab, isAdmin]);
  
  // Reload escrows when filter changes
  useEffect(() => {
    if (!isAdmin || activeCategory !== 'financial' || activeSubTab !== 'escrows') return;
    loadEscrows();
  }, [escrowFilters.status]);

  const loadData = async () => {
    if (activeCategory === 'content' && activeSubTab === 'academy') {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      switch (activeCategory) {
        case 'dashboard':
          await loadDashboardData();
          break;
        case 'users':
          await loadUsers();
          if (activeSubTab === 'suppliers') {
            await loadSuppliers();
          }
          break;
        case 'marketplace':
          if (activeSubTab === 'supplier-products') {
            await loadSupplierProducts();
          // Legacy products loading removed
          } else if (activeSubTab === 'shop-orders') {
            await loadOrders();
          }
          break;
        case 'financial':
          if (activeSubTab === 'withdrawals') {
            await loadWithdrawals();
          } else if (activeSubTab === 'escrows') {
            await loadEscrows();
          } else if (activeSubTab === 'earnings') {
            await loadEarnings();
          }
          break;
        case 'content':
          await loadFeedback();
          break;
      }
    } catch (e) {
      console.error('Load data error:', e);
    } finally {
      setLoading(false);
    }
  };

  const loadDashboardData = async () => {
    const [subRes, supplierSummary, productSummary, revenueOrders, userDist, subStatus, userGrowth] = await Promise.all([
      adminAPI.getSubsSummary().catch(() => ({ data: {} })),
      adminAPI.getSuppliersSummary().catch(() => ({ data: {} })),
      adminAPI.getSupplierProductsSummary().catch(() => ({ data: {} })),
      adminAPI.getRevenueOrdersChart().catch(() => ({ data: { data: [] } })),
      adminAPI.getUserDistributionChart().catch(() => ({ data: { distribution: [] } })),
      adminAPI.getSubscriptionStatusChart().catch(() => ({ data: { status: [] } })),
      adminAPI.getUserGrowthChart().catch(() => ({ data: { data: [] } })),
    ]);
    setDashboardData({
      subscriptions: subRes.data,
      suppliers: supplierSummary.data,
      products: productSummary.data,
    });
    setRevenueOrdersChart(revenueOrders.data.data || []);
    setUserDistributionChart(userDist.data.distribution || []);
    setSubscriptionStatusChart(subStatus.data.status || []);
    setUserGrowthChart(userGrowth.data.data || []);
  };

  const loadUsers = async () => {
    const res = await adminAPI.getUsers({});
    setUsers(res.data.users || []);
  };

  const loadSuppliers = async () => {
    const [suppRes, summaryRes] = await Promise.all([
      adminAPI.getSuppliers({ limit: 100 }),
      adminAPI.getSuppliersSummary(),
    ]);
    setSuppliers(suppRes.data.suppliers || []);
    setSuppliersSummary(summaryRes.data);
  };

  const loadSupplierProducts = async () => {
    const [prodRes, summaryRes] = await Promise.all([
      adminAPI.getSupplierProducts({ limit: 100 }),
      adminAPI.getSupplierProductsSummary(),
    ]);
    setSupplierProducts(prodRes.data.products || []);
    setSupplierProductsSummary(summaryRes.data);
  };

  // loadLegacyProducts removed - no longer needed

  const loadOrders = async () => {
    const res = await marketAPI.adminListOrders({ limit: 100 });
    setOrders(res.data.orders || []);
  };

  const loadWithdrawals = async () => {
    const res = await walletAPI.adminGetWithdrawals({}).catch(() => ({ data: { withdrawals: [] } }));
    setAdminWithdrawals(res.data.withdrawals || []);
  };

  const loadEscrows = async () => {
    // Pass status filter - defaults to 'holding' on both client and server
    const res = await walletAPI.adminGetEscrows({ status: escrowFilters.status || 'holding' }).catch(() => ({ data: { escrows: [] } }));
    setAdminEscrows(res.data.escrows || []);
  };

  const loadEarnings = async () => {
    const res = await adminSettlementsAPI.getOmabraEarnings({ period: earningsPeriod });
    setOmabraEarnings(res.data);
  };

  const loadFeedback = async () => {
    const res = await feedbackAPI.adminList({});
    setFeedback(res.data.feedback || []);
  };

  // Supplier actions
  const handleVerifySupplier = async (supplierId) => {
    setProcessing(supplierId);
    try {
      await adminAPI.verifySupplier(supplierId);
      toast.success('Supplier verified');
      loadSuppliers();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to verify');
    } finally {
      setProcessing(null);
    }
  };

  const handleRejectSupplier = async () => {
    if (!selectedSupplier || !rejectReason) return;
    setProcessing(selectedSupplier.id);
    try {
      await adminAPI.rejectSupplier(selectedSupplier.id, rejectReason);
      toast.success('Supplier rejected');
      setSelectedSupplier(null);
      setRejectReason('');
      loadSuppliers();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to reject');
    } finally {
      setProcessing(null);
    }
  };

  // Product actions
  const handleApproveProduct = async (productId) => {
    setProcessing(productId);
    try {
      await adminAPI.approveProduct(productId);
      toast.success('Product approved');
      loadSupplierProducts();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to approve');
    } finally {
      setProcessing(null);
    }
  };

  const handleRejectProduct = async () => {
    if (!selectedProduct || !rejectReason) return;
    setProcessing(selectedProduct.id);
    try {
      await adminAPI.rejectProduct(selectedProduct.id, rejectReason);
      toast.success('Product rejected');
      setSelectedProduct(null);
      setRejectReason('');
      loadSupplierProducts();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to reject');
    } finally {
      setProcessing(null);
    }
  };

  const handleBulkApprove = async (productIds) => {
    if (!productIds.length) return;
    setProcessing('bulk');
    try {
      await adminAPI.bulkApproveProducts(productIds);
      toast.success(`${productIds.length} products approved`);
      loadSupplierProducts();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to bulk approve');
    } finally {
      setProcessing(null);
    }
  };

  // Filtered and paginated data
  const filteredUsers = useMemo(() => {
    let result = users;
    if (activeSubTab === 'farmers') result = result.filter(u => u.accountType === 'farmer' || (!u.accountType && u.role !== 'admin'));
    else if (activeSubTab === 'suppliers') return []; // Handled separately
    else if (activeSubTab === 'clients') result = result.filter(u => u.accountType === 'client');
    
    if (userFilters.role) result = result.filter(u => u.role === userFilters.role);
    if (userFilters.subscription === 'active') result = result.filter(u => u.subscription?.status === 'active');
    else if (userFilters.subscription === 'expired') result = result.filter(u => u.subscription?.status === 'expired');
    else if (userFilters.subscription === 'none') result = result.filter(u => !u.subscription?.status);
    if (userFilters.search) {
      const q = userFilters.search.toLowerCase();
      result = result.filter(u => 
        `${u.firstName} ${u.lastName}`.toLowerCase().includes(q) ||
        u.email?.toLowerCase().includes(q)
      );
    }
    return result;
  }, [users, activeSubTab, userFilters]);

  const filteredSuppliers = useMemo(() => {
    let result = suppliers;
    if (supplierFilters.verified === 'true') result = result.filter(s => s.isVerified);
    else if (supplierFilters.verified === 'false') result = result.filter(s => !s.isVerified);
    if (supplierFilters.active === 'true') result = result.filter(s => s.isActive);
    else if (supplierFilters.active === 'false') result = result.filter(s => !s.isActive);
    if (supplierFilters.businessType) {
      result = result.filter((s) =>
        s.businessType === supplierFilters.businessType ||
        (Array.isArray(s.businessTypes) && s.businessTypes.includes(supplierFilters.businessType))
      );
    }
    if (supplierFilters.search) {
      const q = supplierFilters.search.toLowerCase();
      result = result.filter(s => 
        s.businessName?.toLowerCase().includes(q) ||
        s.user?.email?.toLowerCase().includes(q) ||
        `${s.user?.firstName} ${s.user?.lastName}`.toLowerCase().includes(q)
      );
    }
    return result;
  }, [suppliers, supplierFilters]);

  const filteredSupplierProducts = useMemo(() => {
    let result = supplierProducts;
    if (supplierProductFilters.approved === 'true') result = result.filter(p => p.isApproved);
    else if (supplierProductFilters.approved === 'false') result = result.filter(p => !p.isApproved);
    if (supplierProductFilters.active === 'true') result = result.filter(p => p.isActive);
    else if (supplierProductFilters.active === 'false') result = result.filter(p => !p.isActive);
    if (supplierProductFilters.category) result = result.filter(p => p.category === supplierProductFilters.category);
    if (supplierProductFilters.search) {
      const q = supplierProductFilters.search.toLowerCase();
      result = result.filter(p => 
        p.name?.toLowerCase().includes(q) ||
        p.supplier?.businessName?.toLowerCase().includes(q)
      );
    }
    return result;
  }, [supplierProducts, supplierProductFilters]);

  // Pagination helpers
  const paginate = (data, page) => {
    const start = (page - 1) * pageSize;
    return data.slice(start, start + pageSize);
  };

  const getTotalPages = (data) => Math.ceil(data.length / pageSize);

  if (!isAdmin) {
    return (
      <div className="text-center py-12">
        <h1 className="text-2xl font-bold text-gray-900">Admin</h1>
        <p className="text-gray-600">You do not have permission to view this page.</p>
      </div>
    );
  }

  const renderPagination = (currentPage, setPage, totalItems, label = 'items') => {
    const totalPages = Math.ceil(totalItems / pageSize);
    if (totalPages <= 1) return null;
    return (
      <div className="flex items-center justify-between px-4 py-3 border-t bg-gray-50">
        <span className="text-sm text-gray-600">
          Showing {Math.min((currentPage - 1) * pageSize + 1, totalItems)} - {Math.min(currentPage * pageSize, totalItems)} of {totalItems} {label}
        </span>
        <div className="flex items-center gap-2">
          <button 
            className="px-3 py-1 text-sm border rounded-lg hover:bg-gray-100 disabled:opacity-50" 
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={currentPage <= 1}
          >
            Previous
          </button>
          <span className="text-sm text-gray-600">Page {currentPage} of {totalPages}</span>
          <button 
            className="px-3 py-1 text-sm border rounded-lg hover:bg-gray-100 disabled:opacity-50" 
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            disabled={currentPage >= totalPages}
          >
            Next
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="-mx-4 -my-4 sm:-mx-6 sm:-my-6 md:mx-0 md:my-0 min-h-screen bg-gray-50">
      <div className="flex flex-col md:flex-row">
        {/* Mobile nav */}
        <div className="md:hidden bg-white border-b sticky top-0 z-20">
          <div className="px-4 pt-3">
            <h1 className="text-lg font-bold text-gray-900">Admin Panel</h1>
          </div>
          <div className="flex gap-1 overflow-x-auto px-2 py-2">
            {CATEGORIES.map((cat) => {
              const Icon = cat.icon;
              const isActive = activeCategory === cat.id;
              const subTabs = SUB_TABS[cat.id] || [];
              return (
                <button
                  key={cat.id}
                  onClick={() => {
                    setActiveCategory(cat.id);
                    setActiveSubTab(subTabs[0]?.id || 'overview');
                  }}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm whitespace-nowrap shrink-0 ${
                    isActive ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  {cat.name}
                </button>
              );
            })}
          </div>
          {(SUB_TABS[activeCategory] || []).length > 1 && (
            <div className="flex gap-1 overflow-x-auto px-2 pb-2 border-t pt-2">
              {(SUB_TABS[activeCategory] || []).map((sub) => (
                <button
                  key={sub.id}
                  onClick={() => setActiveSubTab(sub.id)}
                  className={`px-3 py-1 rounded text-sm whitespace-nowrap shrink-0 ${
                    activeSubTab === sub.id ? 'bg-blue-100 text-blue-700 font-medium' : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  {sub.name}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="hidden md:block w-64 shrink-0 bg-white border-r min-h-screen sticky top-0">
          <div className="p-4 border-b">
            <h1 className="text-xl font-bold text-gray-900">Admin Panel</h1>
            <p className="text-sm text-gray-500">Manage your platform</p>
          </div>
          <nav className="p-2">
            {CATEGORIES.map((cat) => {
              const Icon = cat.icon;
              const isActive = activeCategory === cat.id;
              const subTabs = SUB_TABS[cat.id] || [];
              const pendingCount = cat.id === 'marketplace' ? (supplierProductsSummary?.pending || 0) : 
                                   cat.id === 'users' && activeSubTab === 'suppliers' ? (suppliersSummary?.pending || 0) : 0;
              
              return (
                <div key={cat.id} className="mb-1">
                  <button
                    onClick={() => {
                      setActiveCategory(cat.id);
                      setActiveSubTab(subTabs[0]?.id || 'overview');
                    }}
                    className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-left transition-colors ${
                      isActive ? 'bg-blue-50 text-blue-700' : 'text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <Icon className="h-5 w-5" />
                      <span className="font-medium">{cat.name}</span>
                    </div>
                    {pendingCount > 0 && (
                      <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">{pendingCount}</span>
                    )}
                  </button>
                  
                  {/* Sub-tabs */}
                  {isActive && subTabs.length > 1 && (
                    <div className="ml-8 mt-1 space-y-1">
                      {subTabs.map((sub) => (
                        <button
                          key={sub.id}
                          onClick={() => setActiveSubTab(sub.id)}
                          className={`w-full flex items-center gap-2 px-3 py-1.5 rounded text-sm ${
                            activeSubTab === sub.id ? 'bg-blue-100 text-blue-700 font-medium' : 'text-gray-600 hover:bg-gray-50'
                          }`}
                        >
                          <ChevronRightIcon className="h-3 w-3" />
                          {sub.name}
                          {sub.id === 'suppliers' && suppliersSummary?.pending > 0 && (
                            <span className="bg-amber-500 text-white text-xs px-1.5 py-0.5 rounded-full ml-auto">{suppliersSummary.pending}</span>
                          )}
                          {sub.id === 'supplier-products' && supplierProductsSummary?.pending > 0 && (
                            <span className="bg-amber-500 text-white text-xs px-1.5 py-0.5 rounded-full ml-auto">{supplierProductsSummary.pending}</span>
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </nav>
        </div>

        {/* Main Content */}
        <div className="flex-1 min-w-0 p-3 sm:p-6">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <LoadingSpinner />
            </div>
          ) : (
            <>
              {/* DASHBOARD */}
              {activeCategory === 'dashboard' && (
                <div className="space-y-6">
                  <h2 className="text-2xl font-bold text-gray-900">Dashboard Overview</h2>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div className="bg-white rounded-xl border p-4">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-green-100 rounded-lg">
                          <CurrencyDollarIcon className="h-6 w-6 text-green-600" />
                        </div>
                        <div>
                          <p className="text-sm text-gray-500">Monthly Revenue</p>
                          <p className="text-2xl font-bold">KES {dashboardData?.subscriptions?.total?.toLocaleString() || 0}</p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="bg-white rounded-xl border p-4">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-blue-100 rounded-lg">
                          <BuildingStorefrontIcon className="h-6 w-6 text-blue-600" />
                        </div>
                        <div>
                          <p className="text-sm text-gray-500">Total Suppliers</p>
                          <p className="text-2xl font-bold">{dashboardData?.suppliers?.total || 0}</p>
                          <p className="text-xs text-amber-600">{dashboardData?.suppliers?.pending || 0} pending</p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="bg-white rounded-xl border p-4">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-purple-100 rounded-lg">
                          <CubeIcon className="h-6 w-6 text-purple-600" />
                        </div>
                        <div>
                          <p className="text-sm text-gray-500">Supplier Products</p>
                          <p className="text-2xl font-bold">{dashboardData?.products?.total || 0}</p>
                          <p className="text-xs text-amber-600">{dashboardData?.products?.pending || 0} pending approval</p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="bg-white rounded-xl border p-4">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-amber-100 rounded-lg">
                          <BanknotesIcon className="h-6 w-6 text-amber-600" />
                        </div>
                        <div>
                          <p className="text-sm text-gray-500">Lifetime Revenue</p>
                          <p className="text-2xl font-bold">KES {dashboardData?.subscriptions?.lifetimeTotal?.toLocaleString() || 0}</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Quick Actions */}
                  <div className="bg-white rounded-xl border p-6">
                    <h3 className="font-semibold text-gray-900 mb-4">Quick Actions</h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <button
                        onClick={() => { setActiveCategory('users'); setActiveSubTab('suppliers'); }}
                        className="p-4 border rounded-lg hover:bg-gray-50 text-left"
                      >
                        <BuildingStorefrontIcon className="h-8 w-8 text-blue-600 mb-2" />
                        <p className="font-medium">Review Suppliers</p>
                        <p className="text-sm text-gray-500">{dashboardData?.suppliers?.pending || 0} pending</p>
                      </button>
                      <button
                        onClick={() => { setActiveCategory('marketplace'); setActiveSubTab('supplier-products'); }}
                        className="p-4 border rounded-lg hover:bg-gray-50 text-left"
                      >
                        <CubeIcon className="h-8 w-8 text-purple-600 mb-2" />
                        <p className="font-medium">Approve Products</p>
                        <p className="text-sm text-gray-500">{dashboardData?.products?.pending || 0} pending</p>
                      </button>
                      <button
                        onClick={() => { setActiveCategory('financial'); setActiveSubTab('withdrawals'); }}
                        className="p-4 border rounded-lg hover:bg-gray-50 text-left"
                      >
                        <BanknotesIcon className="h-8 w-8 text-green-600 mb-2" />
                        <p className="font-medium">Withdrawal Requests</p>
                        <p className="text-sm text-gray-500">Process payouts</p>
                      </button>
                      <button
                        onClick={() => { setActiveCategory('financial'); setActiveSubTab('wallet'); }}
                        className="p-4 border rounded-lg hover:bg-gray-50 text-left"
                      >
                        <WalletIcon className="h-8 w-8 text-amber-600 mb-2" />
                        <p className="font-medium">Wallet Admin</p>
                        <p className="text-sm text-gray-500">Withdrawals & escrows</p>
                      </button>
                    </div>
                  </div>

                  {/* Revenue & Orders Chart */}
                  <div className="bg-white rounded-xl border p-6">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
                      <div>
                        <h3 className="font-semibold text-gray-900 flex items-center">
                          <ChartBarIcon className="h-5 w-5 mr-2 text-gray-500" />
                          Revenue & Orders (Past 12 Months)
                        </h3>
                        <p className="text-sm text-gray-500 mt-1">Monthly platform performance</p>
                      </div>
                      <div className="flex gap-6 text-sm">
                        <div className="text-right">
                          <p className="text-gray-500">Total Revenue</p>
                          <p className="font-semibold text-green-600">
                            KES {revenueOrdersChart.reduce((sum, d) => sum + d.totalRevenue, 0).toLocaleString()}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-gray-500">Total Orders</p>
                          <p className="font-semibold text-blue-600">
                            {revenueOrdersChart.reduce((sum, d) => sum + d.totalOrders, 0).toLocaleString()}
                          </p>
                        </div>
                      </div>
                    </div>
                    
                    {revenueOrdersChart.length > 0 ? (
                      <div className="h-72">
                        <ResponsiveContainer width="100%" height="100%">
                          <LineChart data={revenueOrdersChart}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                            <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                            <YAxis yAxisId="left" tick={{ fontSize: 11 }} tickFormatter={(v) => `${(v / 1000).toFixed(0)}K`} />
                            <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} />
                            <Tooltip 
                              formatter={(value, name) => [
                                name.includes('Revenue') ? `KES ${Number(value).toLocaleString()}` : value,
                                name
                              ]}
                            />
                            <Legend />
                            <Line yAxisId="left" type="monotone" dataKey="totalRevenue" name="Revenue" stroke="#10b981" strokeWidth={2} dot={false} />
                            <Line yAxisId="right" type="monotone" dataKey="totalOrders" name="Orders" stroke="#3b82f6" strokeWidth={2} dot={false} />
                          </LineChart>
                        </ResponsiveContainer>
                      </div>
                    ) : (
                      <div className="h-72 flex items-center justify-center text-gray-500">
                        <div className="text-center">
                          <ChartBarIcon className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                          <p>No data available</p>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* User Growth Chart */}
                  <div className="bg-white rounded-xl border p-6">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
                      <div>
                        <h3 className="font-semibold text-gray-900 flex items-center">
                          <UsersIcon className="h-5 w-5 mr-2 text-gray-500" />
                          User Growth (Past 12 Months)
                        </h3>
                        <p className="text-sm text-gray-500 mt-1">New user registrations by type</p>
                      </div>
                      <div className="flex gap-4 text-sm">
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 rounded-full bg-green-500"></div>
                          <span className="text-gray-600">Farmers</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                          <span className="text-gray-600">Suppliers</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 rounded-full bg-amber-500"></div>
                          <span className="text-gray-600">Clients</span>
                        </div>
                      </div>
                    </div>
                    
                    {userGrowthChart.length > 0 ? (
                      <div className="h-64">
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={userGrowthChart}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                            <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                            <YAxis tick={{ fontSize: 11 }} />
                            <Tooltip />
                            <Legend />
                            <Bar dataKey="farmers" name="Farmers" fill="#10b981" stackId="a" />
                            <Bar dataKey="suppliers" name="Suppliers" fill="#3b82f6" stackId="a" />
                            <Bar dataKey="clients" name="Clients" fill="#f59e0b" stackId="a" />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    ) : (
                      <div className="h-64 flex items-center justify-center text-gray-500">
                        <div className="text-center">
                          <UsersIcon className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                          <p>No data available</p>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Distribution Charts Row */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* User Distribution Pie Chart */}
                    <div className="bg-white rounded-xl border p-6">
                      <h3 className="font-semibold text-gray-900 mb-4 flex items-center">
                        <UsersIcon className="h-5 w-5 mr-2 text-gray-500" />
                        User Distribution
                      </h3>
                      {userDistributionChart.length > 0 ? (
                        <div className="h-64">
                          <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                              <Pie
                                data={userDistributionChart}
                                cx="50%"
                                cy="50%"
                                innerRadius={50}
                                outerRadius={80}
                                paddingAngle={2}
                                dataKey="value"
                                label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                              >
                                {userDistributionChart.map((entry, index) => (
                                  <Cell key={`cell-${index}`} fill={entry.color} />
                                ))}
                              </Pie>
                              <Tooltip formatter={(value) => [value, 'Users']} />
                            </PieChart>
                          </ResponsiveContainer>
                        </div>
                      ) : (
                        <div className="h-64 flex items-center justify-center text-gray-500">
                          <p>No data available</p>
                        </div>
                      )}
                      <div className="flex flex-wrap justify-center gap-4 mt-4">
                        {userDistributionChart.map((item, i) => (
                          <div key={i} className="flex items-center gap-2 text-sm">
                            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }}></div>
                            <span className="text-gray-600">{item.name}: {item.value}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Subscription Status Pie Chart */}
                    <div className="bg-white rounded-xl border p-6">
                      <h3 className="font-semibold text-gray-900 mb-4 flex items-center">
                        <CurrencyDollarIcon className="h-5 w-5 mr-2 text-gray-500" />
                        Subscription Status
                      </h3>
                      {subscriptionStatusChart.length > 0 ? (
                        <div className="h-64">
                          <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                              <Pie
                                data={subscriptionStatusChart}
                                cx="50%"
                                cy="50%"
                                innerRadius={50}
                                outerRadius={80}
                                paddingAngle={2}
                                dataKey="value"
                                label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                              >
                                {subscriptionStatusChart.map((entry, index) => (
                                  <Cell key={`cell-${index}`} fill={entry.color} />
                                ))}
                              </Pie>
                              <Tooltip formatter={(value) => [value, 'Users']} />
                            </PieChart>
                          </ResponsiveContainer>
                        </div>
                      ) : (
                        <div className="h-64 flex items-center justify-center text-gray-500">
                          <p>No data available</p>
                        </div>
                      )}
                      <div className="flex flex-wrap justify-center gap-4 mt-4">
                        {subscriptionStatusChart.map((item, i) => (
                          <div key={i} className="flex items-center gap-2 text-sm">
                            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }}></div>
                            <span className="text-gray-600">{item.name}: {item.value}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* USERS - All Users / Farmers / Clients */}
              {activeCategory === 'users' && activeSubTab !== 'suppliers' && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h2 className="text-2xl font-bold text-gray-900">
                      {activeSubTab === 'all-users' ? 'All Users' : activeSubTab === 'farmers' ? 'Farmers' : 'Clients'}
                    </h2>
                    <button onClick={loadUsers} className="px-3 py-1.5 text-sm border rounded-lg hover:bg-gray-50">
                      Refresh
                    </button>
                  </div>

                  {/* Filters */}
                  <div className="bg-white rounded-xl border p-4">
                    <div className="flex flex-wrap items-center gap-3">
                      <div className="flex items-center gap-2">
                        <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
                        <input
                          type="text"
                          placeholder="Search users..."
                          className="px-3 py-1.5 border rounded-lg text-sm w-48"
                          value={userFilters.search}
                          onChange={(e) => { setUserFilters(f => ({ ...f, search: e.target.value })); setUserPage(1); }}
                        />
                      </div>
                      {activeSubTab === 'all-users' && (
                        <select
                          className="px-3 py-1.5 border rounded-lg text-sm"
                          value={userFilters.role}
                          onChange={(e) => { setUserFilters(f => ({ ...f, role: e.target.value })); setUserPage(1); }}
                        >
                          <option value="">All Roles</option>
                          <option value="admin">Admin</option>
                          <option value="user">User</option>
                        </select>
                      )}
                      <select
                        className="px-3 py-1.5 border rounded-lg text-sm"
                        value={userFilters.subscription}
                        onChange={(e) => { setUserFilters(f => ({ ...f, subscription: e.target.value })); setUserPage(1); }}
                      >
                        <option value="">All Subscriptions</option>
                        <option value="active">Active</option>
                        <option value="expired">Expired</option>
                        <option value="none">None</option>
                      </select>
                      {(userFilters.search || userFilters.role || userFilters.subscription) && (
                        <button
                          className="text-sm text-blue-600 hover:underline"
                          onClick={() => { setUserFilters({ role: '', subscription: '', accountType: '', search: '' }); setUserPage(1); }}
                        >
                          Clear filters
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Users Table */}
                  <div className="bg-white rounded-xl border overflow-hidden">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Name</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Email</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Phone</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Type</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Role</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Subscription</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Registered</th>
                          <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {paginate(filteredUsers, userPage).map(u => (
                          <tr key={u.id} className="hover:bg-gray-50">
                            <td className="px-4 py-3 text-sm text-gray-900">{u.firstName} {u.lastName}</td>
                            <td className="px-4 py-3 text-sm text-gray-700">{u.email}</td>
                            <td className="px-4 py-3 text-sm text-gray-700">{u.phone || '—'}</td>
                            <td className="px-4 py-3 text-sm">
                              <span className={`px-2 py-0.5 rounded text-xs font-medium capitalize ${
                                u.accountType === 'supplier' ? 'bg-blue-100 text-blue-700' : 
                                u.accountType === 'client' ? 'bg-amber-100 text-amber-700' : 
                                'bg-green-100 text-green-700'
                              }`}>{u.accountType || 'farmer'}</span>
                            </td>
                            <td className="px-4 py-3 text-sm">
                              <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                u.role === 'admin' ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-700'
                              }`}>{u.role}</span>
                            </td>
                            <td className="px-4 py-3 text-sm">
                              <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                u.subscription?.status === 'active' ? 'bg-green-100 text-green-700' : 
                                u.subscription?.status === 'expired' ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-500'
                              }`}>{u.subscription?.status || 'none'}</span>
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-700">{u.createdAt ? new Date(u.createdAt).toLocaleDateString() : '—'}</td>
                            <td className="px-4 py-3 text-sm text-right">
                              {u.role !== 'admin' && (
                                <div className="flex items-center justify-end gap-1">
                                  <button
                                    className="px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700"
                                    onClick={async () => {
                                      const days = parseInt(prompt('Days to extend:', '30') || '0');
                                      if (!days) return;
                                      try {
                                        await adminAPI.activateSubscription(u.id, days);
                                        toast.success('Subscription extended');
                                        loadUsers();
                                      } catch (e) {
                                        toast.error(e.response?.data?.message || 'Failed');
                                      }
                                    }}
                                  >
                                    +Paid
                                  </button>
                                  <button
                                    className="px-2 py-1 text-xs border border-green-500 text-green-600 rounded hover:bg-green-50"
                                    onClick={async () => {
                                      const days = parseInt(prompt('Free days:', '7') || '0');
                                      if (!days) return;
                                      try {
                                        await adminAPI.extendSubscriptionFree(u.id, days);
                                        toast.success('Extended (free)');
                                        loadUsers();
                                      } catch (e) {
                                        toast.error(e.response?.data?.message || 'Failed');
                                      }
                                    }}
                                  >
                                    +Free
                                  </button>
                                </div>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {renderPagination(userPage, setUserPage, filteredUsers.length, 'users')}
                  </div>
                </div>
              )}

              {/* USERS - Suppliers */}
              {activeCategory === 'users' && activeSubTab === 'suppliers' && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h2 className="text-2xl font-bold text-gray-900">Suppliers</h2>
                    <button onClick={loadSuppliers} className="px-3 py-1.5 text-sm border rounded-lg hover:bg-gray-50">
                      Refresh
                    </button>
                  </div>

                  {/* Summary Cards */}
                  {suppliersSummary && (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="bg-white rounded-xl border p-4">
                        <p className="text-sm text-gray-500">Total</p>
                        <p className="text-2xl font-bold">{suppliersSummary.total}</p>
                      </div>
                      <div className="bg-white rounded-xl border p-4">
                        <p className="text-sm text-gray-500">Verified</p>
                        <p className="text-2xl font-bold text-green-600">{suppliersSummary.verified}</p>
                      </div>
                      <div className="bg-white rounded-xl border p-4">
                        <p className="text-sm text-gray-500">Pending Verification</p>
                        <p className="text-2xl font-bold text-amber-600">{suppliersSummary.pending}</p>
                      </div>
                      <div className="bg-white rounded-xl border p-4">
                        <p className="text-sm text-gray-500">Active</p>
                        <p className="text-2xl font-bold text-blue-600">{suppliersSummary.active}</p>
                      </div>
                    </div>
                  )}

                  {/* Filters */}
                  <div className="bg-white rounded-xl border p-4">
                    <div className="flex flex-wrap items-center gap-3">
                      <div className="flex items-center gap-2">
                        <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
                        <input
                          type="text"
                          placeholder="Search suppliers..."
                          className="px-3 py-1.5 border rounded-lg text-sm w-48"
                          value={supplierFilters.search}
                          onChange={(e) => { setSupplierFilters(f => ({ ...f, search: e.target.value })); setSupplierPage(1); }}
                        />
                      </div>
                      <select
                        className="px-3 py-1.5 border rounded-lg text-sm"
                        value={supplierFilters.verified}
                        onChange={(e) => { setSupplierFilters(f => ({ ...f, verified: e.target.value })); setSupplierPage(1); }}
                      >
                        <option value="">All Status</option>
                        <option value="true">Verified</option>
                        <option value="false">Pending</option>
                      </select>
                      <select
                        className="px-3 py-1.5 border rounded-lg text-sm"
                        value={supplierFilters.businessType}
                        onChange={(e) => { setSupplierFilters(f => ({ ...f, businessType: e.target.value })); setSupplierPage(1); }}
                      >
                        <option value="">All Types</option>
                        <option value="feed">Feed</option>
                        <option value="equipment">Equipment</option>
                        <option value="medication">Medication</option>
                        <option value="hatchery">Hatchery</option>
                        <option value="veterinary">Veterinary</option>
                        <option value="other">Other</option>
                      </select>
                      {(supplierFilters.search || supplierFilters.verified || supplierFilters.businessType) && (
                        <button
                          className="text-sm text-blue-600 hover:underline"
                          onClick={() => { setSupplierFilters({ verified: '', active: '', businessType: '', search: '' }); setSupplierPage(1); }}
                        >
                          Clear filters
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Suppliers Table */}
                  <div className="bg-white rounded-xl border overflow-hidden">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Business</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Owner</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Type</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Location</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Products</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Status</th>
                          <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {paginate(filteredSuppliers, supplierPage).map(s => (
                          <tr key={s.id} className={`hover:bg-gray-50 ${!s.isVerified ? 'bg-amber-50' : ''}`}>
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-3">
                                {s.logoUrl ? (
                                  <img src={s.logoUrl} alt="" className="w-10 h-10 rounded-lg object-cover" />
                                ) : (
                                  <div className="w-10 h-10 rounded-lg bg-gray-200 flex items-center justify-center">
                                    <BuildingStorefrontIcon className="h-5 w-5 text-gray-400" />
                                  </div>
                                )}
                                <div>
                                  <p className="font-medium text-gray-900">{s.businessName}</p>
                                  <p className="text-xs text-gray-500">{s.user?.email}</p>
                                </div>
                              </div>
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-700">
                              {s.user?.firstName} {s.user?.lastName}
                            </td>
                            <td className="px-4 py-3 text-sm">
                              <span className="px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700 capitalize">
                                {formatBusinessTypes(s)}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-700">
                              {s.town || s.county || '—'}
                            </td>
                            <td className="px-4 py-3 text-sm">
                              {s.pendingProducts > 0 && (
                                <span className="px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-700">
                                  {s.pendingProducts} pending
                                </span>
                              )}
                            </td>
                            <td className="px-4 py-3 text-sm">
                              {s.isVerified ? (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700">
                                  <CheckCircleIcon className="h-3 w-3" /> Verified
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-700">
                                  <ClockIcon className="h-3 w-3" /> Pending
                                </span>
                              )}
                            </td>
                            <td className="px-4 py-3 text-sm text-right">
                              <div className="flex items-center justify-end gap-1">
                                {!s.isVerified ? (
                                  <>
                                    <button
                                      onClick={() => handleVerifySupplier(s.id)}
                                      disabled={processing === s.id}
                                      className="px-2 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
                                    >
                                      {processing === s.id ? '...' : 'Verify'}
                                    </button>
                                    <button
                                      onClick={() => { setSelectedSupplier(s); setRejectReason(''); }}
                                      className="px-2 py-1 text-xs bg-red-600 text-white rounded hover:bg-red-700"
                                    >
                                      Reject
                                    </button>
                                  </>
                                ) : (
                                  <button
                                    onClick={() => { setSelectedSupplier(s); setRejectReason(''); }}
                                    className="px-2 py-1 text-xs border border-red-500 text-red-600 rounded hover:bg-red-50"
                                  >
                                    Revoke
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {renderPagination(supplierPage, setSupplierPage, filteredSuppliers.length, 'suppliers')}
                  </div>
                </div>
              )}

              {/* MARKETPLACE - Supplier Products */}
              {activeCategory === 'marketplace' && activeSubTab === 'supplier-products' && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h2 className="text-2xl font-bold text-gray-900">Supplier Products</h2>
                    <div className="flex items-center gap-2">
                      {filteredSupplierProducts.filter(p => !p.isApproved).length > 0 && (
                        <button
                          onClick={() => handleBulkApprove(filteredSupplierProducts.filter(p => !p.isApproved).map(p => p.id))}
                          disabled={processing === 'bulk'}
                          className="px-3 py-1.5 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                        >
                          {processing === 'bulk' ? 'Approving...' : `Approve All Pending (${filteredSupplierProducts.filter(p => !p.isApproved).length})`}
                        </button>
                      )}
                      <button onClick={loadSupplierProducts} className="px-3 py-1.5 text-sm border rounded-lg hover:bg-gray-50">
                        Refresh
                      </button>
                    </div>
                  </div>

                  {/* Summary Cards */}
                  {supplierProductsSummary && (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="bg-white rounded-xl border p-4">
                        <p className="text-sm text-gray-500">Total Products</p>
                        <p className="text-2xl font-bold">{supplierProductsSummary.total}</p>
                      </div>
                      <div className="bg-white rounded-xl border p-4">
                        <p className="text-sm text-gray-500">Approved</p>
                        <p className="text-2xl font-bold text-green-600">{supplierProductsSummary.approved}</p>
                      </div>
                      <div className="bg-white rounded-xl border p-4">
                        <p className="text-sm text-gray-500">Pending Approval</p>
                        <p className="text-2xl font-bold text-amber-600">{supplierProductsSummary.pending}</p>
                      </div>
                      <div className="bg-white rounded-xl border p-4">
                        <p className="text-sm text-gray-500">Active</p>
                        <p className="text-2xl font-bold text-blue-600">{supplierProductsSummary.active}</p>
                      </div>
                    </div>
                  )}

                  {/* Filters */}
                  <div className="bg-white rounded-xl border p-4">
                    <div className="flex flex-wrap items-center gap-3">
                      <div className="flex items-center gap-2">
                        <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
                        <input
                          type="text"
                          placeholder="Search products..."
                          className="px-3 py-1.5 border rounded-lg text-sm w-48"
                          value={supplierProductFilters.search}
                          onChange={(e) => { setSupplierProductFilters(f => ({ ...f, search: e.target.value })); setSupplierProductPage(1); }}
                        />
                      </div>
                      <select
                        className="px-3 py-1.5 border rounded-lg text-sm"
                        value={supplierProductFilters.approved}
                        onChange={(e) => { setSupplierProductFilters(f => ({ ...f, approved: e.target.value })); setSupplierProductPage(1); }}
                      >
                        <option value="">All Status</option>
                        <option value="true">Approved</option>
                        <option value="false">Pending</option>
                      </select>
                      <select
                        className="px-3 py-1.5 border rounded-lg text-sm"
                        value={supplierProductFilters.category}
                        onChange={(e) => { setSupplierProductFilters(f => ({ ...f, category: e.target.value })); setSupplierProductPage(1); }}
                      >
                        <option value="">All Categories</option>
                        <option value="feed">Feed</option>
                        <option value="equipment">Equipment</option>
                        <option value="medication">Medication</option>
                        <option value="chicks">Day-old Chicks</option>
                        <option value="other">Other</option>
                      </select>
                      {(supplierProductFilters.search || supplierProductFilters.approved || supplierProductFilters.category) && (
                        <button
                          className="text-sm text-blue-600 hover:underline"
                          onClick={() => { setSupplierProductFilters({ approved: '', active: '', category: '', search: '' }); setSupplierProductPage(1); }}
                        >
                          Clear filters
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Products Table */}
                  <div className="bg-white rounded-xl border overflow-hidden">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Product</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Supplier</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Category</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Price</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Status</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Created</th>
                          <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {paginate(filteredSupplierProducts, supplierProductPage).map(p => (
                          <tr key={p.id} className={`hover:bg-gray-50 ${!p.isApproved ? 'bg-amber-50' : ''}`}>
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-3">
                                {p.images?.[0] ? (
                                  <img src={p.images[0]} alt="" className="w-12 h-12 rounded-lg object-cover" />
                                ) : (
                                  <div className="w-12 h-12 rounded-lg bg-gray-200 flex items-center justify-center">
                                    <PhotoIcon className="h-6 w-6 text-gray-400" />
                                  </div>
                                )}
                                <div>
                                  <p className="font-medium text-gray-900">{p.name}</p>
                                  <p className="text-xs text-gray-500">{p.brand || '—'}</p>
                                </div>
                              </div>
                            </td>
                            <td className="px-4 py-3 text-sm">
                              <p className="text-gray-900">{p.supplier?.businessName}</p>
                              <p className="text-xs text-gray-500">{p.supplier?.user?.email}</p>
                            </td>
                            <td className="px-4 py-3 text-sm">
                              <span className="px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700 capitalize">
                                {p.category}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-sm font-medium text-gray-900">
                              KES {Number(p.price).toLocaleString()}
                              {p.unit && <span className="text-gray-500 font-normal">/{p.unit}</span>}
                            </td>
                            <td className="px-4 py-3 text-sm">
                              {p.isApproved ? (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700">
                                  <CheckCircleIcon className="h-3 w-3" /> Approved
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-700">
                                  <ClockIcon className="h-3 w-3" /> Pending
                                </span>
                              )}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-700">
                              {p.createdAt ? new Date(p.createdAt).toLocaleDateString() : '—'}
                            </td>
                            <td className="px-4 py-3 text-sm text-right">
                              <div className="flex items-center justify-end gap-1">
                                {!p.isApproved ? (
                                  <>
                                    <button
                                      onClick={() => handleApproveProduct(p.id)}
                                      disabled={processing === p.id}
                                      className="px-2 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
                                    >
                                      {processing === p.id ? '...' : 'Approve'}
                                    </button>
                                    <button
                                      onClick={() => { setSelectedProduct(p); setRejectReason(''); }}
                                      className="px-2 py-1 text-xs bg-red-600 text-white rounded hover:bg-red-700"
                                    >
                                      Reject
                                    </button>
                                  </>
                                ) : (
                                  <button
                                    onClick={() => { setSelectedProduct(p); setRejectReason(''); }}
                                    className="px-2 py-1 text-xs border border-red-500 text-red-600 rounded hover:bg-red-50"
                                  >
                                    Revoke
                                  </button>
                                )}
                                <button
                                  onClick={async () => {
                                    if (!window.confirm('Delete this product?')) return;
                                    try {
                                      await adminAPI.deleteSupplierProduct(p.id);
                                      toast.success('Product deleted');
                                      loadSupplierProducts();
                                    } catch (e) {
                                      toast.error('Failed to delete');
                                    }
                                  }}
                                  className="px-2 py-1 text-xs border border-gray-300 text-gray-600 rounded hover:bg-gray-50"
                                >
                                  Delete
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {renderPagination(supplierProductPage, setSupplierProductPage, filteredSupplierProducts.length, 'products')}
                  </div>
                </div>
              )}

              {/* Legacy Products and Shop Orders sections removed */}

              {/* FINANCIAL - Withdrawal Requests */}
              {activeCategory === 'financial' && activeSubTab === 'withdrawals' && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h2 className="text-2xl font-bold text-gray-900">Withdrawal Requests</h2>
                    <button onClick={loadWithdrawals} className="px-3 py-1.5 text-sm border rounded-lg hover:bg-gray-50">
                      Refresh
                    </button>
                  </div>

                  {/* Summary Cards */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="bg-amber-50 rounded-xl border border-amber-200 p-4">
                      <p className="text-sm text-amber-700">Pending Requests</p>
                      <p className="text-2xl font-bold text-amber-800">{adminWithdrawals.filter(w => w.status === 'pending').length}</p>
                    </div>
                    <div className="bg-white rounded-xl border p-4">
                      <p className="text-sm text-gray-500">Total Pending Amount</p>
                      <p className="text-2xl font-bold">KES {adminWithdrawals.filter(w => w.status === 'pending').reduce((sum, w) => sum + Number(w.amount), 0).toLocaleString()}</p>
                    </div>
                    <div className="bg-green-50 rounded-xl border border-green-200 p-4">
                      <p className="text-sm text-green-700">Completed Today</p>
                      <p className="text-2xl font-bold text-green-800">{adminWithdrawals.filter(w => w.status === 'completed' && new Date(w.completedAt).toDateString() === new Date().toDateString()).length}</p>
                    </div>
                  </div>

                  {/* Filters */}
                  <div className="bg-white rounded-xl border p-4">
                    <div className="flex flex-wrap items-center gap-3">
                      <div className="flex items-center gap-2">
                        <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
                        <input
                          type="text"
                          placeholder="Search by name or request #..."
                          className="px-3 py-1.5 border rounded-lg text-sm w-48"
                          value={withdrawalFilters.search}
                          onChange={(e) => { setWithdrawalFilters(f => ({ ...f, search: e.target.value })); setWithdrawalPage(1); }}
                        />
                      </div>
                      <select
                        className="px-3 py-1.5 border rounded-lg text-sm"
                        value={withdrawalFilters.status}
                        onChange={(e) => { setWithdrawalFilters(f => ({ ...f, status: e.target.value })); setWithdrawalPage(1); }}
                      >
                        <option value="">All Status</option>
                        <option value="pending">Pending</option>
                        <option value="cancel_requested">Cancel Requested</option>
                        <option value="completed">Completed</option>
                        <option value="rejected">Rejected</option>
                      </select>
                    </div>
                  </div>

                  <div className="bg-white rounded-xl border overflow-hidden">
                    {adminWithdrawals.length === 0 ? (
                      <div className="p-8 text-center text-gray-500">No withdrawal requests</div>
                    ) : (
                      <>
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Request #</th>
                              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">User</th>
                              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Amount</th>
                              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Method</th>
                              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Status</th>
                              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Date</th>
                              <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600">Actions</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200">
                            {paginate(
                              adminWithdrawals.filter(w => {
                                if (withdrawalFilters.status && w.status !== withdrawalFilters.status) return false;
                                if (withdrawalFilters.search) {
                                  const q = withdrawalFilters.search.toLowerCase();
                                  return w.requestNumber?.toLowerCase().includes(q) || `${w.user?.firstName} ${w.user?.lastName}`.toLowerCase().includes(q) || w.user?.email?.toLowerCase().includes(q);
                                }
                                return true;
                              }),
                              withdrawalPage
                            ).map(w => (
                              <tr key={w.id} className={w.status === 'pending' ? 'bg-amber-50' : 'hover:bg-gray-50'}>
                                <td className="px-4 py-3 text-sm font-medium text-gray-900">{w.requestNumber}</td>
                                <td className="px-4 py-3 text-sm">
                                  <div className="font-medium">{w.user ? `${w.user.firstName} ${w.user.lastName}` : 'Unknown'}</div>
                                  <div className="text-xs text-gray-500">{w.user?.email || '—'}</div>
                                  <div className="text-xs text-gray-400 capitalize">{w.user?.accountType || '—'}</div>
                                </td>
                                <td className="px-4 py-3 text-sm font-semibold text-gray-900">KES {Number(w.amount).toLocaleString()}</td>
                                <td className="px-4 py-3 text-sm">
                                  <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                    w.withdrawalMethod === 'mpesa' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'
                                  }`}>
                                    {w.withdrawalMethod === 'mpesa' ? '📱 M-Pesa' : '🏦 Bank'}
                                  </span>
                                  <div className="text-xs text-gray-500 mt-0.5">
                                    {w.withdrawalMethod === 'mpesa' ? w.mpesaPhone : `${w.bankName} - ${w.bankAccountNumber}`}
                                  </div>
                                </td>
                                <td className="px-4 py-3 text-sm">
                                  <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                    w.status === 'pending' ? 'bg-amber-100 text-amber-700' :
                                    w.status === 'completed' ? 'bg-green-100 text-green-700' :
                                    w.status === 'cancel_requested' ? 'bg-purple-100 text-purple-700' :
                                    'bg-red-100 text-red-700'
                                  }`}>
                                    {w.status === 'cancel_requested' ? 'Cancel Requested' : w.status.charAt(0).toUpperCase() + w.status.slice(1)}
                                  </span>
                                  {w.userNote && w.status === 'cancel_requested' && (
                                    <p className="text-xs text-gray-500 mt-0.5">Reason: {w.userNote}</p>
                                  )}
                                </td>
                                <td className="px-4 py-3 text-sm text-gray-500">
                                  {new Date(w.createdAt).toLocaleDateString()}
                                </td>
                                <td className="px-4 py-3 text-sm text-right">
                                  {w.status === 'pending' && (
                                    <div className="flex justify-end gap-1">
                                      <button
                                        onClick={() => setCompleteWithdrawalModal(w)}
                                        className="px-2 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700"
                                      >
                                        Complete
                                      </button>
                                      <button
                                        onClick={async () => {
                                          const reason = prompt('Rejection reason:');
                                          if (!reason) return;
                                          try {
                                            await walletAPI.adminRejectWithdrawal(w.id, { reason });
                                            toast.success('Withdrawal rejected');
                                            loadWithdrawals();
                                          } catch (e) {
                                            toast.error(e.response?.data?.error || 'Failed');
                                          }
                                        }}
                                        className="px-2 py-1 text-xs border border-red-300 text-red-600 rounded hover:bg-red-50"
                                      >
                                        Reject
                                      </button>
                                    </div>
                                  )}
                                  {w.status === 'cancel_requested' && (
                                    <div className="flex justify-end gap-1">
                                      <button
                                        onClick={async () => {
                                          if (!window.confirm('Approve cancellation and refund the user?')) return;
                                          try {
                                            await walletAPI.adminApproveCancelWithdrawal(w.id);
                                            toast.success('Cancellation approved. Funds returned to user.');
                                            loadWithdrawals();
                                          } catch (e) {
                                            toast.error(e.response?.data?.error || 'Failed');
                                          }
                                        }}
                                        className="px-2 py-1 text-xs bg-purple-600 text-white rounded hover:bg-purple-700"
                                      >
                                        Approve Cancel
                                      </button>
                                      <button
                                        onClick={async () => {
                                          const reason = prompt('Reason for denying cancellation:');
                                          if (reason === null) return;
                                          try {
                                            await walletAPI.adminRejectCancelWithdrawal(w.id, { reason });
                                            toast.success('Cancellation denied. Withdrawal remains pending.');
                                            loadWithdrawals();
                                          } catch (e) {
                                            toast.error(e.response?.data?.error || 'Failed');
                                          }
                                        }}
                                        className="px-2 py-1 text-xs border border-gray-300 text-gray-600 rounded hover:bg-gray-50"
                                      >
                                        Deny Cancel
                                      </button>
                                    </div>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        {renderPagination(withdrawalPage, setWithdrawalPage, adminWithdrawals.filter(w => !withdrawalFilters.status || w.status === withdrawalFilters.status).length, 'withdrawals')}
                      </>
                    )}
                  </div>
                </div>
              )}

              {/* FINANCIAL - Escrow Management */}
              {activeCategory === 'financial' && activeSubTab === 'escrows' && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h2 className="text-2xl font-bold text-gray-900">Escrow Management</h2>
                    <button onClick={loadEscrows} className="px-3 py-1.5 text-sm border rounded-lg hover:bg-gray-50">
                      Refresh
                    </button>
                  </div>

                  {/* Summary Cards */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="bg-blue-50 rounded-xl border border-blue-200 p-4">
                      <p className="text-sm text-blue-700">In Holding</p>
                      <p className="text-2xl font-bold text-blue-800">{adminEscrows.filter(e => e.status === 'holding').length}</p>
                    </div>
                    <div className="bg-white rounded-xl border p-4">
                      <p className="text-sm text-gray-500">Total Held Amount</p>
                      <p className="text-2xl font-bold">KES {adminEscrows.filter(e => e.status === 'holding').reduce((sum, e) => sum + Number(e.totalAmount), 0).toLocaleString()}</p>
                    </div>
                    <div className="bg-green-50 rounded-xl border border-green-200 p-4">
                      <p className="text-sm text-green-700">Released Today</p>
                      <p className="text-2xl font-bold text-green-800">{adminEscrows.filter(e => e.status === 'released' && e.releasedAt && new Date(e.releasedAt).toDateString() === new Date().toDateString()).length}</p>
                    </div>
                  </div>

                  {/* Filters */}
                  <div className="bg-white rounded-xl border p-4">
                    <div className="flex flex-wrap items-center gap-3">
                      <select
                        className="px-3 py-1.5 border rounded-lg text-sm"
                        value={escrowFilters.status}
                        onChange={(e) => { setEscrowFilters(f => ({ ...f, status: e.target.value })); setEscrowPage(1); }}
                      >
                        <option value="holding">Holding (Pending)</option>
                        <option value="all">All Status</option>
                        <option value="released">Released</option>
                        <option value="refunded">Refunded</option>
                      </select>
                      <select
                        className="px-3 py-1.5 border rounded-lg text-sm"
                        value={escrowFilters.orderType}
                        onChange={(e) => { setEscrowFilters(f => ({ ...f, orderType: e.target.value })); setEscrowPage(1); }}
                      >
                        <option value="">All Types</option>
                        <option value="ShopOrder">Shop Orders</option>
                        <option value="MarketOrder">Supplier Orders</option>
                      </select>
                    </div>
                  </div>

                  <div className="bg-white rounded-xl border overflow-hidden">
                    {adminEscrows.length === 0 ? (
                      <div className="p-8 text-center text-gray-500">No escrows found</div>
                    ) : (
                      <>
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Order</th>
                              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Type</th>
                              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Amount</th>
                              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Seller Gets</th>
                              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Status</th>
                              <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Auto Release</th>
                              <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600">Actions</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200">
                            {paginate(
                              adminEscrows.filter(e => {
                                if (escrowFilters.status && e.status !== escrowFilters.status) return false;
                                if (escrowFilters.orderType && e.orderType !== escrowFilters.orderType) return false;
                                return true;
                              }),
                              escrowPage
                            ).map(e => (
                              <tr key={e.id} className={e.status === 'holding' ? 'bg-blue-50' : 'hover:bg-gray-50'}>
                                <td className="px-4 py-3 text-sm font-medium text-gray-900">
                                  {e.orderId?.slice(0, 8)}...
                                </td>
                                <td className="px-4 py-3 text-sm">
                                  <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                    e.orderType === 'ShopOrder' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'
                                  }`}>
                                    {e.orderType === 'ShopOrder' ? '🛒 Shop' : '📦 Supplier'}
                                  </span>
                                  <div className="text-xs text-gray-500 mt-0.5">
                                    Seller: {e.sellerWallet?.user ? `${e.sellerWallet.user.firstName} ${e.sellerWallet.user.lastName}` : 'Unknown'}
                                  </div>
                                </td>
                                <td className="px-4 py-3 text-sm font-semibold">KES {Number(e.totalAmount).toLocaleString()}</td>
                                <td className="px-4 py-3 text-sm text-green-600 font-medium">KES {Number(e.sellerAmount).toLocaleString()}</td>
                                <td className="px-4 py-3 text-sm">
                                  <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                    e.status === 'holding' ? 'bg-blue-100 text-blue-700' :
                                    e.status === 'released' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                                  }`}>
                                    {e.status.charAt(0).toUpperCase() + e.status.slice(1)}
                                  </span>
                                </td>
                                <td className="px-4 py-3 text-sm text-gray-500">
                                  {e.autoReleaseAt ? new Date(e.autoReleaseAt).toLocaleDateString() : '-'}
                                </td>
                                <td className="px-4 py-3 text-sm text-right">
                                  {e.status === 'holding' && (
                                    <div className="flex justify-end gap-1">
                                      <button
                                        onClick={async () => {
                                          if (!window.confirm('Release this escrow to the seller?')) return;
                                          try {
                                            await walletAPI.adminReleaseEscrow(e.id, { releaseNote: 'Admin manual release' });
                                            toast.success('Escrow released');
                                            loadEscrows();
                                          } catch (err) {
                                            console.error('Release escrow error:', err);
                                            toast.error(err.response?.data?.error || err.message || 'Failed to release escrow');
                                          }
                                        }}
                                        className="px-2 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700"
                                      >
                                        Release
                                      </button>
                                      <button
                                        onClick={async () => {
                                          const reason = prompt('Refund reason:');
                                          if (!reason) return;
                                          try {
                                            await walletAPI.adminRefundEscrow(e.id, { refundReason: reason });
                                            toast.success('Escrow refunded');
                                            loadEscrows();
                                          } catch (err) {
                                            console.error('Refund escrow error:', err);
                                            toast.error(err.response?.data?.error || err.message || 'Failed to refund escrow');
                                          }
                                        }}
                                        className="px-2 py-1 text-xs border border-red-300 text-red-600 rounded hover:bg-red-50"
                                      >
                                        Refund
                                      </button>
                                    </div>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        {renderPagination(escrowPage, setEscrowPage, adminEscrows.filter(e => (!escrowFilters.status || e.status === escrowFilters.status) && (!escrowFilters.orderType || e.orderType === escrowFilters.orderType)).length, 'escrows')}
                      </>
                    )}
                  </div>
                </div>
              )}

              {/* FINANCIAL - Platform Earnings */}
              {activeCategory === 'financial' && activeSubTab === 'earnings' && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h2 className="text-2xl font-bold text-gray-900">Platform Earnings</h2>
                    <div className="flex items-center gap-2">
                      <select
                        className="px-3 py-1.5 border rounded-lg text-sm"
                        value={earningsPeriod}
                        onChange={(e) => { setEarningsPeriod(e.target.value); loadEarnings(); }}
                      >
                        <option value="today">Today</option>
                        <option value="week">This Week</option>
                        <option value="month">This Month</option>
                        <option value="all">All Time</option>
                      </select>
                      <button onClick={loadEarnings} className="px-3 py-1.5 text-sm border rounded-lg hover:bg-gray-50">
                        Refresh
                      </button>
                    </div>
                  </div>

                  {omabraEarnings && (
                    <>
                      {/* Summary Cards */}
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        <div className="bg-white rounded-xl border p-4">
                          <p className="text-sm text-gray-500">Gross Transactions</p>
                          <p className="text-2xl font-bold">KES {omabraEarnings.earnings?.totalGrossTransactions?.toLocaleString() || 0}</p>
                          <p className="text-xs text-gray-400">{omabraEarnings.earnings?.orderCount || 0} orders</p>
                        </div>
                        <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-xl p-4 text-white">
                          <p className="text-sm text-green-100">💰 Total Platform Fees</p>
                          <p className="text-2xl font-bold">KES {omabraEarnings.earnings?.totalPlatformFees?.toLocaleString() || 0}</p>
                        </div>
                        <div className="bg-white rounded-xl border p-4">
                          <p className="text-sm text-gray-500">Seller Payouts</p>
                          <p className="text-2xl font-bold">KES {omabraEarnings.earnings?.totalFarmerPayouts?.toLocaleString() || 0}</p>
                        </div>
                        <div className="bg-white rounded-xl border p-4">
                          <p className="text-sm text-gray-500">Holdings (Unsettled)</p>
                          <p className="text-2xl font-bold text-amber-600">KES {omabraEarnings.holdings?.pendingSettlements?.toLocaleString() || 0}</p>
                        </div>
                      </div>

                      {/* Fees Breakdown by Source */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="bg-blue-50 rounded-xl border border-blue-100 p-4">
                          <div className="flex items-center gap-2 mb-2">
                            <span className="text-lg">🛒</span>
                            <p className="text-sm font-medium text-blue-800">Shop Orders (Client → Farmer)</p>
                          </div>
                          <p className="text-xl font-bold text-blue-900">KES {omabraEarnings.earnings?.shopFees?.toLocaleString() || 0}</p>
                          <p className="text-xs text-blue-600">{omabraEarnings.earnings?.shopOrders || 0} orders</p>
                        </div>
                        <div className="bg-purple-50 rounded-xl border border-purple-100 p-4">
                          <div className="flex items-center gap-2 mb-2">
                            <span className="text-lg">📦</span>
                            <p className="text-sm font-medium text-purple-800">Supplier Orders (Farmer → Supplier)</p>
                          </div>
                          <p className="text-xl font-bold text-purple-900">KES {omabraEarnings.earnings?.supplierFees?.toLocaleString() || 0}</p>
                          <p className="text-xs text-purple-600">{omabraEarnings.earnings?.supplierOrders || 0} orders</p>
                        </div>
                      </div>

                      {/* Holdings Breakdown */}
                      {(omabraEarnings.holdings?.shopPending > 0 || omabraEarnings.holdings?.supplierPending > 0) && (
                        <div className="bg-amber-50 rounded-xl border border-amber-100 p-4">
                          <p className="text-sm font-medium text-amber-800 mb-2">Pending Settlements Breakdown</p>
                          <div className="flex gap-6">
                            <div>
                              <p className="text-xs text-amber-600">Shop Orders</p>
                              <p className="font-semibold text-amber-900">KES {omabraEarnings.holdings?.shopPending?.toLocaleString() || 0}</p>
                            </div>
                            <div>
                              <p className="text-xs text-amber-600">Supplier Orders</p>
                              <p className="font-semibold text-amber-900">KES {omabraEarnings.holdings?.supplierPending?.toLocaleString() || 0}</p>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Daily Breakdown Table */}
                      {omabraEarnings.dailyBreakdown?.length > 0 && (
                        <div className="bg-white rounded-xl border overflow-hidden">
                          <div className="p-4 border-b">
                            <h3 className="font-semibold text-gray-900">Daily Breakdown</h3>
                          </div>
                          <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                              <thead className="bg-gray-50">
                                <tr>
                                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Date</th>
                                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Orders</th>
                                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Gross Amount</th>
                                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Total Fees</th>
                                  <th className="px-4 py-3 text-left text-xs font-semibold text-blue-600">Shop Fees</th>
                                  <th className="px-4 py-3 text-left text-xs font-semibold text-purple-600">Supplier Fees</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-gray-200">
                                {paginate(omabraEarnings.dailyBreakdown, earningsPage).map((d, i) => (
                                  <tr key={i} className="hover:bg-gray-50">
                                    <td className="px-4 py-3 text-sm text-gray-900">{d.date}</td>
                                    <td className="px-4 py-3 text-sm text-gray-700">
                                      {d.orders}
                                      <span className="text-xs text-gray-400 ml-1">
                                        ({d.shopOrders || 0} + {d.supplierOrders || 0})
                                      </span>
                                    </td>
                                    <td className="px-4 py-3 text-sm text-gray-700">KES {d.grossAmount?.toLocaleString()}</td>
                                    <td className="px-4 py-3 text-sm font-medium text-green-600">KES {d.platformFees?.toLocaleString()}</td>
                                    <td className="px-4 py-3 text-sm text-blue-600">KES {d.shopFees?.toLocaleString() || 0}</td>
                                    <td className="px-4 py-3 text-sm text-purple-600">KES {d.supplierFees?.toLocaleString() || 0}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                          {renderPagination(earningsPage, setEarningsPage, omabraEarnings.dailyBreakdown.length, 'days')}
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}

              {/* CONTENT - Feedback / Academy */}
              {activeCategory === 'content' && activeSubTab === 'feedback' && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h2 className="text-2xl font-bold text-gray-900">Feedback</h2>
                    <button onClick={loadFeedback} className="px-3 py-1.5 text-sm border rounded-lg hover:bg-gray-50">
                      Refresh
                    </button>
                  </div>

                  <div className="bg-white rounded-xl border overflow-hidden">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Date</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">User</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Farm</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Category</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Message</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {paginate(feedback, feedbackPage).map(f => (
                          <tr key={f.id} className="hover:bg-gray-50">
                            <td className="px-4 py-3 text-sm text-gray-700">{new Date(f.createdAt).toLocaleDateString()}</td>
                            <td className="px-4 py-3 text-sm text-gray-700">{f.user?.firstName} {f.user?.lastName}</td>
                            <td className="px-4 py-3 text-sm text-gray-700">{f.farm?.name || '—'}</td>
                            <td className="px-4 py-3 text-sm">
                              <span className="px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700">
                                {f.category || 'general'}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-700 max-w-md truncate">{f.message}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {renderPagination(feedbackPage, setFeedbackPage, feedback.length, 'feedback')}
                  </div>
                </div>
              )}

              {activeCategory === 'content' && activeSubTab === 'academy' && (
                <AdminAcademy />
              )}
            </>
          )}
        </div>
      </div>

      {/* Reject Supplier Modal */}
      {selectedSupplier && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
            <div className="p-4 border-b">
              <h3 className="font-semibold text-gray-900">
                {selectedSupplier.isVerified ? 'Revoke Verification' : 'Reject Supplier'}
              </h3>
            </div>
            <div className="p-4 space-y-4">
              <p className="text-sm text-gray-600">
                {selectedSupplier.isVerified 
                  ? `Revoke verification for "${selectedSupplier.businessName}"?`
                  : `Reject "${selectedSupplier.businessName}"?`
                }
              </p>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Reason *</label>
                <textarea
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border rounded-lg"
                  placeholder="Explain why..."
                />
              </div>
              <div className="flex justify-end gap-2">
                <button
                  onClick={() => { setSelectedSupplier(null); setRejectReason(''); }}
                  className="px-4 py-2 text-sm border rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleRejectSupplier}
                  disabled={!rejectReason || processing}
                  className="px-4 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
                >
                  {processing ? 'Processing...' : selectedSupplier.isVerified ? 'Revoke' : 'Reject'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Reject Product Modal */}
      {selectedProduct && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
            <div className="p-4 border-b">
              <h3 className="font-semibold text-gray-900">
                {selectedProduct.isApproved ? 'Revoke Approval' : 'Reject Product'}
              </h3>
            </div>
            <div className="p-4 space-y-4">
              <p className="text-sm text-gray-600">
                {selectedProduct.isApproved 
                  ? `Revoke approval for "${selectedProduct.name}"?`
                  : `Reject "${selectedProduct.name}"?`
                }
              </p>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Reason *</label>
                <textarea
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border rounded-lg"
                  placeholder="Explain what needs to be fixed..."
                />
              </div>
              <div className="flex justify-end gap-2">
                <button
                  onClick={() => { setSelectedProduct(null); setRejectReason(''); }}
                  className="px-4 py-2 text-sm border rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleRejectProduct}
                  disabled={!rejectReason || processing}
                  className="px-4 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
                >
                  {processing ? 'Processing...' : selectedProduct.isApproved ? 'Revoke' : 'Reject'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Complete Withdrawal Modal */}
      {completeWithdrawalModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
            <div className="p-4 border-b">
              <h3 className="font-semibold text-gray-900">Complete Withdrawal</h3>
            </div>
            <form
              onSubmit={async (e) => {
                e.preventDefault();
                const formData = new FormData(e.target);
                try {
                  await walletAPI.adminCompleteWithdrawal(completeWithdrawalModal.id, {
                    transactionReference: formData.get('transactionReference'),
                    transactionFee: Number(formData.get('transactionFee') || 0),
                    adminNote: formData.get('adminNote')
                  });
                  toast.success('Withdrawal completed');
                  setCompleteWithdrawalModal(null);
                  loadWithdrawals();
                } catch (e) {
                  toast.error(e.response?.data?.error || e.response?.data?.message || 'Failed');
                }
              }}
              className="p-4 space-y-4"
            >
              <div className="bg-gray-50 p-3 rounded-lg text-sm">
                <div><span className="text-gray-500">Request:</span> {completeWithdrawalModal.requestNumber}</div>
                <div><span className="text-gray-500">Amount:</span> <span className="font-semibold text-green-700">KES {Number(completeWithdrawalModal.amount).toLocaleString()}</span></div>
                <div><span className="text-gray-500">Method:</span> {completeWithdrawalModal.withdrawalMethod === 'mpesa' ? `M-Pesa to ${completeWithdrawalModal.mpesaPhone}` : `Bank to ${completeWithdrawalModal.bankAccountNumber}`}</div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Transaction Reference *</label>
                <input name="transactionReference" required className="w-full px-3 py-2 border rounded-lg" placeholder="M-Pesa receipt or bank ref" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Transaction Fee</label>
                <input name="transactionFee" type="number" step="0.01" defaultValue="0" className="w-full px-3 py-2 border rounded-lg" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Admin Note</label>
                <textarea name="adminNote" rows={2} className="w-full px-3 py-2 border rounded-lg" />
              </div>
              <div className="flex justify-end gap-2">
                <button type="button" onClick={() => setCompleteWithdrawalModal(null)} className="px-4 py-2 text-sm border rounded-lg hover:bg-gray-50">
                  Cancel
                </button>
                <button type="submit" className="px-4 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700">
                  Mark Completed
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Order Detail Modal */}
      {orderDetail && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl">
            <div className="p-4 border-b flex justify-between">
              <h3 className="font-semibold text-gray-900">Order {orderDetail.id.slice(0, 8)}</h3>
              <button onClick={() => setOrderDetail(null)} className="text-gray-500 hover:text-gray-700">✕</button>
            </div>
            <div className="p-6 grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-gray-500">Customer</p>
                <p className="font-medium">{orderDetail.user ? `${orderDetail.user.firstName || ''} ${orderDetail.user.lastName || ''}`.trim() || orderDetail.user.email : '—'}</p>
              </div>
              <div>
                <p className="text-gray-500">Contact</p>
                <p className="font-medium">{orderDetail.contactName} • {orderDetail.phone}</p>
              </div>
              <div>
                <p className="text-gray-500">Product</p>
                <p className="font-medium">{orderDetail.product?.title || '—'}</p>
              </div>
              <div>
                <p className="text-gray-500">Quantity</p>
                <p className="font-medium">{orderDetail.quantity}</p>
              </div>
              <div>
                <p className="text-gray-500">Total</p>
                <p className="font-medium">KES {(Number(orderDetail.unitPrice || orderDetail.product?.price || 0) * orderDetail.quantity).toLocaleString()}</p>
              </div>
              <div>
                <p className="text-gray-500">Status</p>
                <p className="font-medium">{orderDetail.status}</p>
              </div>
              <div className="col-span-2">
                <p className="text-gray-500">Address</p>
                <p className="font-medium">{orderDetail.addressLine1}, {orderDetail.city}</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Admin;
